import { Component, Inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { CargoheatingCommonService } from '../../service/cargo-heating-service/cargoheating-common.service';
import { RequestFormService } from '../../service/requestForm/request-form.service';
import { resolveCargoHeatingVesselRules } from '../../constants/cargo-heating-vessel-rules';

export type IngestionTab = 'daily_log' | 'tank_profile';

export interface DailyLogConflict {
  date: string;
  field: string;
  db_value: string | number;
  incoming_value: string | number;
  tank?: string;
}

export interface IngestResponse {
  message?: string;
  status?: string;
  inserted_dates?: string[];
}

@Component({
  selector: 'app-daily-log-insert-popup',
  templateUrl: './daily-log-insert-popup.component.html',
  styleUrls: ['./daily-log-insert-popup.component.scss'],
})
export class DailyLogInsertPopupComponent {
  activeTab: IngestionTab = 'daily_log';
  selectedFile: File | null = null;
  isSubmitting = false;

  get canInsert(): boolean {
    return !!this.selectedFile && !this.isSubmitting;
  }

  constructor(
    private dialogRef: MatDialogRef<DailyLogInsertPopupComponent>,
    private toastr: ToastrService,
    private commonService: CargoheatingCommonService,
    private requestFormService: RequestFormService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      voyageId?: string;
      vesselName?: string;
      vesselImo?: string;
      initialTab?: IngestionTab;
      /** Voyage already has data: only daily readings may be ingested until logs are cleared. */
      tankProfileLocked?: boolean;
    },
  ) {
    if (this.data?.initialTab) {
      this.activeTab = this.data.initialTab;
    }
    if (this.tankProfileLocked) {
      this.activeTab = 'daily_log';
    }
  }

  get tankProfileLocked(): boolean {
    return !!this.data?.tankProfileLocked;
  }

  isDarkMode(): boolean {
    return this.requestFormService ? this.requestFormService.isDarkMode() : false;
  }

  get vesselRulesLabel(): string {
    return (
      resolveCargoHeatingVesselRules(this.data?.vesselName, this.data?.vesselImo)
        ?.label ?? ''
    );
  }

  setTab(tab: IngestionTab): void {
    if (this.isSubmitting || (tab === 'tank_profile' && this.tankProfileLocked)) {
      return;
    }
    this.activeTab = tab;
    this.selectedFile = null;
  }

  uploadFile(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xls,.xlsx,.xlsm,.csv';

    input.onchange = (event: Event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) {
        return;
      }

      this.selectedFile = file;
    };

    input.click();
  }

  executeInsert(): void {
    if (!this.selectedFile) {
      return;
    }

    const voyageId = this.data?.voyageId?.trim();
    if (!voyageId) {
      this.toastr.error('Voyage ID not available.');
      return;
    }

    this.isSubmitting = true;

    if (this.activeTab === 'tank_profile') {
      this.commonService
        .insertTankProfile({
          voyageId,
          tempLog: this.selectedFile,
        })
        .subscribe({
          next: (body: unknown) => {
            this.isSubmitting = false;
            this.handleTankProfileResponse(body);
          },
          error: (err: HttpErrorResponse) => {
            this.isSubmitting = false;
            this.toastr.error(this.extractErrorMessage(err));
          },
        });
    } else {
      this.commonService
        .insertDailyLogs({
          voyageId,
          tempLog: this.selectedFile,
        })
        .subscribe({
          next: (body: unknown) => {
            this.isSubmitting = false;
            this.handleDailyLogResponse(body);
          },
          error: (err: HttpErrorResponse) => {
            this.isSubmitting = false;
            this.toastr.error(this.extractErrorMessage(err));
          },
        });
    }
  }

  closeModal(): void {
    if (this.isSubmitting) {
      return;
    }
    this.dialogRef.close({ success: false });
  }

  private handleDailyLogResponse(body: unknown): void {
    const response = this.extractResponse(body);

    if (this.isSuccessStatus(response.status)) {
      this.toastr.success(this.buildSuccessMessage(response));
      this.dialogRef.close({ success: true, type: 'daily_log' });
      return;
    }

    const fallbackMessage =
      response.message ||
      (body as { message?: string })?.message ||
      'Daily logs inserted successfully';
    this.toastr.success(fallbackMessage);
    this.dialogRef.close({ success: true, type: 'daily_log' });
  }

  private handleTankProfileResponse(body: unknown): void {
    const response = this.extractResponse(body);
    const message =
      response.message ||
      (body as { message?: string })?.message ||
      'Tank profile initialized successfully';
    this.toastr.success(message);
    this.dialogRef.close({ success: true, type: 'tank_profile' });
  }

  private isSuccessStatus(status?: string): boolean {
    const normalized = (status || '').trim().toLowerCase();
    return normalized === 'success' || normalized === 'ok';
  }

  private buildSuccessMessage(response: IngestResponse): string {
    const dates = (response.inserted_dates || []).filter(Boolean);
    if (dates.length > 0) {
      return `Daily logs inserted successfully (${dates.length} ${dates.length === 1 ? 'day' : 'days'} ingested).`;
    }
    return response.message || 'Daily logs inserted successfully.';
  }

  private extractResponse(body: unknown): IngestResponse {
    if (!body || typeof body !== 'object') {
      return {};
    }

    const root = body as Record<string, unknown>;
    const merged = this.unwrapResponseEnvelope(root);

    return {
      status: this.readString(merged['status'] ?? root['status']),
      message: this.readString(merged['message'] ?? root['message']),
      inserted_dates: this.extractStringArray(
        merged['inserted_dates'] ?? root['inserted_dates'],
      ),
    };
  }

  private unwrapResponseEnvelope(
    payload: Record<string, unknown>,
  ): Record<string, unknown> {
    let current = { ...payload };
    const nested = payload['response'];

    if (!nested || typeof nested !== 'object' || Array.isArray(nested)) {
      return current;
    }

    const inner = nested as Record<string, unknown>;
    current = { ...payload, ...inner };
    delete current['response'];

    const doubleNested = inner['response'];
    if (
      doubleNested &&
      typeof doubleNested === 'object' &&
      !Array.isArray(doubleNested)
    ) {
      current = { ...current, ...(doubleNested as Record<string, unknown>) };
      delete current['response'];
    }

    return current;
  }

  private readString(value: unknown): string {
    if (value == null) {
      return '';
    }
    return String(value).trim();
  }

  private extractStringArray(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((entry) => this.readString(entry))
      .filter((entry) => !!entry);
  }

  private extractErrorMessage(err: HttpErrorResponse): string {
    const body = err?.error;

    if (body && typeof body === 'object') {
      const nested = (body as Record<string, unknown>)['response'];
      if (nested && typeof nested === 'object') {
        const message = (nested as Record<string, unknown>)['message'];
        if (typeof message === 'string' && message.trim()) {
          return message;
        }
      }

      return (
        (body as Record<string, unknown>)['message'] ||
        (body as Record<string, unknown>)['detail'] ||
        (body as Record<string, unknown>)['error'] ||
        'Request failed'
      ) as string;
    }

    if (typeof body === 'string' && body.trim()) {
      const plainText = body
        .replace(/<style[\s\S]*?<\/style>/gi, ' ')
        .replace(/<script[\s\S]*?<\/script>/gi, ' ')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      if (plainText) {
        return plainText.length > 220
          ? `${plainText.slice(0, 220)}...`
          : plainText;
      }
    }

    return err?.message || 'Request failed';
  }
}
